import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, CrewMemberReassignment, ForemanCrewGroup, IsoDate, reassignmentOverlapsExisting } from '../../shared'

export interface CrewReassignmentModalContext {
    memberHrRef: number
    memberName: string
    fromForemanHrRef: number
    fromForemanName: string
    foremen: ForemanCrewGroup[]
    existing: CrewMemberReassignment[]
    defaultStartDate: IsoDate
}

@Component({
    selector: 'app-foreman-schedule-crew-reassignment-modal',
    templateUrl: './foreman-schedule-crew-reassignment-modal.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
})
export class ForemanScheduleCrewReassignmentModalComponent extends BaseComponent {
    readonly activeModal = inject(NgbActiveModal)

    context!: CrewReassignmentModalContext

    readonly toForemanHrRef = signal(0)
    readonly startDate = signal<IsoDate>('')
    readonly endDate = signal<IsoDate>('')
    readonly error = signal<string | null>(null)

    readonly targetForemen = signal<ForemanCrewGroup[]>([])

    initFromContext(context: CrewReassignmentModalContext) {
        this.context = context
        this.targetForemen.set((context.foremen ?? []).filter(f => f.hrRef !== context.fromForemanHrRef))
        this.startDate.set(context.defaultStartDate)
        this.endDate.set(context.defaultStartDate)
        const firstTarget = this.targetForemen()[0]
        if (firstTarget) this.toForemanHrRef.set(firstTarget.hrRef)
    }

    save() {
        this.error.set(null)
        const ctx = this.context
        const toForemanHrRef = this.toForemanHrRef()
        const startDate = this.startDate()
        const endDate = this.endDate()

        if (!toForemanHrRef) {
            this.error.set('Select a foreman to assign this crew member to.')
            return
        }
        if (!startDate || !endDate) {
            this.error.set('Start and end dates are required.')
            return
        }
        if (endDate < startDate) {
            this.error.set('End date must be on or after the start date.')
            return
        }
        if (reassignmentOverlapsExisting(ctx.memberHrRef, startDate, endDate, ctx.existing)) {
            this.error.set('This crew member already has a reassignment that overlaps those dates.')
            return
        }

        const toForeman = ctx.foremen.find(f => f.hrRef === toForemanHrRef)
        if (!toForeman) {
            this.error.set('Selected foreman was not found.')
            return
        }

        const reassignment: CrewMemberReassignment = {
            id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `reassign-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            memberHrRef: ctx.memberHrRef,
            memberName: ctx.memberName,
            fromForemanHrRef: ctx.fromForemanHrRef,
            fromForemanName: ctx.fromForemanName,
            toForemanHrRef,
            toForemanName: toForeman.name,
            startDate,
            endDate,
        }

        this.activeModal.close(reassignment)
    }

    cancel() {
        this.activeModal.dismiss()
    }
}
